import { DS_BREAKPOINT } from '@calm/evexia-design-tokens';
import { MediaAuthor, MediaNarrator } from '@calm/evexia-web-sdk';
import { ContributorAvatar } from '@rhg/components/contributor/avatar';
import { ContributorType } from '@rhg/components/contributor/types';
import commonMessages from '@rhg/lang/messages/common';
import { Divider, Stack, Theme, Typography } from '@rhg/libs/material';
import SimpleDialog from '@rhg/libs/material/simple-dialog';
import { useId, useMemo } from 'react';
import { useIntl } from 'react-intl';

export interface ContributorDialogProps {
    contributor?: MediaNarrator | MediaAuthor;
    contributorType?: ContributorType;
    isOpen: boolean;
    onClose: () => void;
}

const styleAvatar = {
    height: (theme: Theme) => theme.spacing(91),
    width: (theme: Theme) => theme.spacing(91),
};

const styleContributorName = { mt: (theme: Theme) => theme.spacing(8) };

const styleDivider = {
    width: '100%',
    mb: (theme: Theme) => theme.ds.spacingUnits.SECTION,
    mt: (theme: Theme) => theme.spacing(6),
};

const styleBio = {
    color: (theme: Theme) => theme.palette.text.sub,
    mb: (theme: Theme) => theme.ds.spacingUnits.CONTENT,
};

const styleContributorType = {
    color: (theme: Theme) => theme.palette.text.sub,
};

const styleContentMaxWidth = {
    maxWidth: { xs: '100%', md: DS_BREAKPOINT.MEDIUM },
};

export function ContributorDialog({
    contributor,
    contributorType,
    isOpen,
    onClose,
}: ContributorDialogProps) {
    const id = useId();
    const { formatMessage } = useIntl();

    const subtitle = useMemo(
        () =>
            contributorType === ContributorType.Narrator
                ? formatMessage(commonMessages['common.narrator'])
                : formatMessage(commonMessages['common.clinical_author']),
        [contributorType, formatMessage]
    );

    const license = useMemo(
        () =>
            contributorType === ContributorType.Author &&
            (contributor as MediaAuthor)?.license
                ? (contributor as MediaAuthor)?.license
                : '',
        [contributor, contributorType]
    );

    const bioChunks = contributor?.bio?.split('\\n\\n');

    return (
        <SimpleDialog
            trackDialog="ProgramContributor"
            background="content"
            aria-labelledby={id}
            content={
                <Stack alignItems="center">
                    <ContributorAvatar
                        alt=""
                        contributor={contributor}
                        sx={styleAvatar}
                    />
                    <Typography
                        id={id}
                        variant="h4"
                        color="text.sub"
                        sx={styleContributorName}
                        align="center"
                    >
                        {contributor?.name}
                    </Typography>
                    <Typography
                        variant="subtitle1"
                        component="p"
                        align="center"
                        sx={styleContributorType}
                    >
                        {subtitle}
                    </Typography>
                    {license && (
                        <Typography
                            component="p"
                            variant="subtitle2"
                            align="center"
                            sx={styleContributorType}
                        >
                            {formatMessage(commonMessages['common.license'], {
                                license,
                            })}
                        </Typography>
                    )}
                    <Divider orientation="horizontal" sx={styleDivider} />
                    {bioChunks?.map((chunk, index) => (
                        <Typography
                            key={`bio_${index}`}
                            variant="subtitle1"
                            component="p"
                            sx={styleBio}
                        >
                            {chunk}
                        </Typography>
                    ))}
                </Stack>
            }
            fullWidth
            fullScreen
            isOpen={isOpen}
            onClose={onClose}
            contentWrapperStyle={styleContentMaxWidth}
        />
    );
}
